/**
 * DraftPlane — the live DRAW-mode creation preview.
 *
 * A fixed always-mounted rect (world space) whose geometry + visibility
 * derive from shared values the draw worklet writes per frame — the same
 * zero-reconciliation pattern as GuidePlane/MarqueePlane. The preview is a
 * rectangle regardless of tool (the committed object renders its real shape
 * one frame later); a per-tool ghost renderer is a documented follow-up.
 *
 * @module floorplan/renderer/DraftPlane
 * @category FloorPlan
 */

import type { SharedValue } from 'react-native-reanimated';
import { useDerivedValue } from 'react-native-reanimated';
import { Rect } from '@shopify/react-native-skia';

/** Shared-value bundle the draw gesture writes (world coords). */
export type DraftChannels = {
  active: SharedValue<number>;
  x: SharedValue<number>;
  y: SharedValue<number>;
  width: SharedValue<number>;
  height: SharedValue<number>;
};

type Props = {
  draft: DraftChannels;
  /** Preview color (theme.accent). */
  color: string;
  /** Zoom snapshot for constant-ish screen stroke width. */
  zoom: number;
};

const DraftPlane = ({ draft, color, zoom }: Props) => {
  // Normalize live so a right-to-left drag previews correctly.
  const x = useDerivedValue(() =>
    Math.min(draft.x.get(), draft.x.get() + draft.width.get()),
  );
  const y = useDerivedValue(() =>
    Math.min(draft.y.get(), draft.y.get() + draft.height.get()),
  );
  const width = useDerivedValue(() => Math.abs(draft.width.get()));
  const height = useDerivedValue(() => Math.abs(draft.height.get()));
  const fillOpacity = useDerivedValue(() => draft.active.get() * 0.15);
  const strokeOpacity = useDerivedValue(() => draft.active.get() * 0.9);

  return (
    <>
      <Rect
        x={x}
        y={y}
        width={width}
        height={height}
        color={color}
        opacity={fillOpacity}
      />
      <Rect
        x={x}
        y={y}
        width={width}
        height={height}
        color={color}
        style="stroke"
        strokeWidth={1.5 / zoom}
        opacity={strokeOpacity}
      />
    </>
  );
};

export default DraftPlane;
